import { Icon } from '@ps/isp-web-component-library';
import { Ninety } from '@ps/isp-web-component-library/icons/Ninety';
import { TwoUpIcon } from '@ps/isp-web-component-library/icons/TwoUpIcon';
import type { MyBetPart, ResultType } from '@ps/isp-web-services-types';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { getIsMatchOddsNinetyMarket, getIsTwoUpMarket } from '@ps/web-sports-utils';
import classNames from 'clsx';
import React from 'react';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import PriceBoost from '@swf/generated/sports-icons/priceBoost';
import StarSub from '@swf/generated/sports-icons/starSub';
import { isOddsBoostMarket } from '@swf/sports/utils/isOddsBoostMarket';

import { isActiveEventBasedMyBetPart } from '../../../../../../../../utils/isActivePart';
import BetTracking from '../../../BetTracking/BetTracking';
import EventTypeIcon from '../EventTypeIcon/EventTypeIcon';
import MarketStatus from '../MarketStatus/MarketStatus';
import SelectionName from '../SelectionName/SelectionName';

import {
  TXTWCL_Match_Odds_90,
  TXTWCL_Match_Odds___2_Up,
  TXTWCL_Star_Sub_enabled_selection,
  TXTWCL_odds_boost
} from './SgmPart.messages.json';
import styles from './SgmPart.scss';

export interface Props {
  part: MyBetPart;
  result?: ResultType;
  isSettled?: boolean;
  cashedOutAmount?: number;
}

const SgmPart: React.FC<Props> = ({ part, result, isSettled, cashedOutAmount }) => {
  const { formatMessage } = useRegulatoryIntl();
  const { outcomeDefinitionExp, sportId, eventId, selectionId } = part;
  const isEnhancedBetTrackingEnabled = useFeature(FeatureFlag.ISPEnableEnhancedBetTracking);
  const eventTypeId = Number(sportId);
  const displayBetTracking =
    isEnhancedBetTrackingEnabled && !isSettled && !cashedOutAmount && !result;
  const isMatchOddsNinetyMarket = getIsMatchOddsNinetyMarket(part.marketType);
  const isTwoUpMarket = getIsTwoUpMarket(part.marketType);

  return (
    <div className={styles.wrapper}>
      <EventTypeIcon isSgm />

      <div
        className={classNames(styles.selectionWrapper, { [styles.voidResult]: result === 'VOID' })}
      >
        <span className={styles.selectionName}>
          <SelectionName part={part} />
          {isActiveEventBasedMyBetPart(part) && part.isStarSubEnabled && (
            <Icon
              className={styles.marketIcon}
              icon={StarSub}
              label={formatMessage(TXTWCL_Star_Sub_enabled_selection)}
              size="xs"
            />
          )}
          {isMatchOddsNinetyMarket ? (
            <Icon
              icon={Ninety}
              className={styles.marketIcon}
              label={formatMessage(TXTWCL_Match_Odds_90)}
              size="xs"
            />
          ) : null}
          {isTwoUpMarket ? (
            <Icon
              icon={TwoUpIcon}
              className={styles.marketIcon}
              label={formatMessage(TXTWCL_Match_Odds___2_Up)}
              size="xs"
            />
          ) : null}
          {isOddsBoostMarket(part.marketType) && (
            <Icon
              className={styles.oddsboostIcon}
              icon={PriceBoost}
              label={formatMessage(TXTWCL_odds_boost)}
              size="xs"
            />
          )}
        </span>
        <span className={styles.selectionDescription}>{part.eventMarketTemplateDescription}</span>
        {displayBetTracking && (
          <BetTracking
            outcomeDefinitionExp={outcomeDefinitionExp}
            eventId={eventId}
            eventTypeId={eventTypeId}
            selectionId={selectionId}
          />
        )}
      </div>
      <MarketStatus result={result} />
    </div>
  );
};

export default SgmPart;
